import React from 'react';
import GenericSection from '../components/sections/GenericSection';
import SectionHeader from '../components/sections/partials/SectionHeader';

const PrivacyPolicy = () => {
  const sectionHeader = {
    title: 'Privacy Policy',
    paragraph: 'Last updated: March 2026'
  };

  return (
    <GenericSection>
      <div className="container-xs">
        <SectionHeader tag="h1" data={sectionHeader} />

        <h3>1. Introduction</h3>
        <p>
          This Privacy Policy explains how we collect, use, and protect personal data in connection with our service
          (&quot;Service&quot;), which integrates with WooCommerce stores to enable natural language querying of store data.
        </p>
        <p>
          We are committed to protecting personal data in accordance with applicable data protection laws, including the GDPR.
        </p>

        <hr />

        <h3>2. Roles and Responsibilities</h3>
        <h4>2.1 Data Controller</h4>
        <p>The WooCommerce store owner (customer) is the data controller of any personal data contained in their store.</p>

        <h4>2.2 Data Processor</h4>
        <p>We act as a data processor, processing data only on behalf of and under the instructions of the customer.</p>

        <hr />

        <h3>3. Data We Process</h3>
        <h4>3.1 Account Data</h4>
        <ul>
          <li>Email address</li>
          <li>Login credentials</li>
        </ul>

        <h4>3.2 User Inputs (Prompts)</h4>
        <p>User queries are processed and stored in anonymised form.</p>
        <ul>
          <li>Personal data is obfuscated (e.g. <code>john@email.com</code> -&gt; <code>j***n@e***l.com</code>)</li>
        </ul>

        <h4>3.3 Database Structure</h4>
        <p>We process schema information only:</p>
        <ul>
          <li>Table names</li>
          <li>Column names</li>
          <li>Database type/version</li>
        </ul>
        <p>No raw database records are extracted during this step.</p>

        <h4>3.4 Query Results</h4>
        <ul>
          <li>Processed transiently</li>
          <li>Not stored</li>
        </ul>

        <hr />

        <h3>4. Use of AI Providers</h3>
        <p>We use third-party AI providers to:</p>
        <ul>
          <li>Convert natural language into SQL</li>
          <li>Format outputs</li>
        </ul>

        <h4>Safeguards:</h4>
        <ul>
          <li>No raw personal data is sent</li>
          <li>PII is masked or excluded</li>
          <li>Schema contains no personal data values</li>
        </ul>

        <hr />

        <h3>5. Purpose of Processing</h3>
        <ul>
          <li>Provide query functionality</li>
          <li>Generate SQL queries</li>
          <li>Display results</li>
          <li>Maintain and improve the service</li>
        </ul>

        <hr />

        <h3>6. Legal Basis</h3>
        <ul>
          <li>Contract (Art. 6(1)(b))</li>
          <li>Legitimate interest (Art. 6(1)(f))</li>
        </ul>

        <hr />

        <h3>7. Data Protection Measures</h3>
        <ul>
          <li>PII masking before AI processing</li>
          <li>No storage of query results</li>
          <li>Read-only database access</li>
          <li>Tenant-based separation</li>
          <li>Role-based access control</li>
        </ul>

        <hr />

        <h3>8. Data Retention</h3>
        <p>We store:</p>
        <ul>
          <li>Anonymised prompts</li>
          <li>SQL queries</li>
          <li>ER models</li>
        </ul>
        <p>Retention is limited to operational necessity (typically up to 30 days for logs).</p>

        <hr />

        <h3>9. Data Security</h3>
        <ul>
          <li>EU-based hosting (AWS)</li>
          <li>Authentication and access control</li>
          <li>Tenant isolation</li>
          <li>Secure processing pipelines</li>
        </ul>

        <hr />

        <h3>10. Data Subject Rights</h3>
        <p>Data subjects have rights including:</p>
        <ul>
          <li>Access</li>
          <li>Rectification</li>
          <li>Erasure</li>
          <li>Restriction</li>
          <li>Portability</li>
          <li>Objection</li>
        </ul>
        <p>Requests should be directed to the store owner.</p>

        <hr />

        <h3>11. Subprocessors</h3>
        <p>We use:</p>
        <ul>
          <li>AI providers</li>
          <li>Cloud infrastructure providers</li>
        </ul>
        <p>Details available upon request.</p>

        <hr />

        <h3>12. Cookies</h3>
        <p>Only essential cookies are used.</p>

        <hr />

        <h3>13. Changes</h3>
        <p>We may update this policy periodically.</p>

        <hr />

        <h3>14. Contact</h3>
        <p className="mb-0">Exanta AI</p>
        <p className="mt-8">
          <a href="mailto:support@exanta.ai">support@exanta.ai</a>
        </p>
      </div>
    </GenericSection>
  );
};

export default PrivacyPolicy;
